/**
 * Datenreife-Helper (#1184 v2.569.0) — Investor-Portal global.
 *
 * Regel: Buchungen brauchen ~1 Monat zum vollständigen Eingang. Ein Monat M ist
 *   - belastbar      wenn heute (Europe/Vienna) ≥ M-Ende + 1 Monat   (Distanz ≥ 2)
 *   - vorläufig      wenn M = Vormonat                                (Distanz = 1)
 *   - unvollständig  wenn M = aktueller Monat oder Zukunft           (Distanz ≤ 0)
 *
 * Beispiel heute = 2026-05-24:
 *   Jan/Feb/März ✅ belastbar · April ⏳ vorläufig · Mai 🔴 unvollständig
 *
 * Stichtag durchgehend Europe/Vienna (gegen UTC-Shift bei Monatswechsel).
 */

/** Today in Europe/Vienna timezone. Returns {year, month, day} (month 1-12). */
export function todayVienna(d = new Date()) {
  const parts = new Intl.DateTimeFormat('de-AT', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (k) => parseInt(parts.find((p) => p.type === k).value, 10);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/** Maturity of a single (year, month). Returns 'belastbar' | 'vorlaeufig' | 'unvollstaendig'. */
export function monthMaturity(year, month, today = new Date()) {
  const v = todayVienna(today);
  const currentYM = v.year * 12 + (v.month - 1);
  const targetYM = year * 12 + (month - 1);
  const distance = currentYM - targetYM;
  if (distance >= 2) return 'belastbar';
  if (distance === 1) return 'vorlaeufig';
  return 'unvollstaendig';
}

/**
 * Last belastbar month within a given year.
 * - year < currentYear → 12 (Dec, full year mature)
 * - year > currentYear → null
 * - year === currentYear → currentMonth − 2, clamped ≥ 1 or null
 */
export function lastMatureMonth(year, today = new Date()) {
  const v = todayVienna(today);
  if (year < v.year) return 12;
  if (year > v.year) return null;
  const last = v.month - 2;
  return last >= 1 ? last : null;
}

/**
 * Default month-state for a segment page: last mature month of current year.
 * Fallback to December of previous year if current year has no mature month yet
 * (e.g. early January where Nov of prior year is the most recent mature month).
 */
export function defaultMatureMonthState(today = new Date()) {
  const v = todayVienna(today);
  const last = lastMatureMonth(v.year, today);
  if (last !== null) return { year: v.year, month: last };
  return { year: v.year - 1, month: 12 };
}

/** Month labels DE (for banner text). */
export const MONTH_LABELS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/**
 * Returns the effective YTD-cutoff month for a given year.
 * - year < currentYear → 12
 * - year > currentYear → null
 * - else: last mature month (= currentMonth − 2, clamped)
 */
export function ytdCutoffMonth(year, today = new Date()) {
  return lastMatureMonth(year, today);
}

/**
 * Evaluates banner context for the current period selection.
 * Returns one of:
 *  - { level: 'red',    title, message }   → aktueller Monat (unvollständig)
 *  - { level: 'amber',  title, message }   → Vormonat (vorläufig) ODER Quartal/Custom enthält akt/vor-Monat
 *  - { level: 'info',   title, message }   → YTD oder Range mit Cutoff-Hinweis
 *  - null                                  → belastbarer Einzelmonat / Vorjahr — kein Banner nötig
 *
 * periodState: { period, year, month, quarter, fromDate, toDate }
 */
export function evaluateMaturityBanner(periodState, today = new Date()) {
  const { period, year, month, quarter, fromDate, toDate } = periodState;
  if (!period) return null;
  const v = todayVienna(today);
  const cutoff = lastMatureMonth(year, today);
  const cutoffLabel = cutoff
    ? `${MONTH_LABELS_DE[cutoff - 1]} ${year}`
    : `${MONTH_LABELS_DE[11]} ${year - 1}`;

  if (period === 'month' && month) {
    const m = monthMaturity(year, month, today);
    if (m === 'unvollstaendig') {
      return {
        level: 'red',
        title: 'Aktueller Monat unvollständig',
        message: `Der gewählte Monat ${MONTH_LABELS_DE[month - 1]} ${year} ist noch nicht abgeschlossen (Buchungen laufen ~1 Monat nach). Werte sind NICHT belastbar.`,
      };
    }
    if (m === 'vorlaeufig') {
      return {
        level: 'amber',
        title: 'Vorläufige Werte',
        message: `Buchungen für ${MONTH_LABELS_DE[month - 1]} ${year} sind noch nicht vollständig (Eingang läuft ~1 Monat nach). Werte sind vorläufig.`,
      };
    }
    return null;
  }

  if (period === 'quarter' && quarter) {
    const qFrom = (quarter - 1) * 3 + 1;
    const qTo = qFrom + 2;
    const fromM = monthMaturity(year, qFrom, today);
    const toM = monthMaturity(year, qTo, today);
    const containsUnvoll = [qFrom, qFrom + 1, qTo].some((m) => monthMaturity(year, m, today) === 'unvollstaendig');
    const containsVorl = [qFrom, qFrom + 1, qTo].some((m) => monthMaturity(year, m, today) === 'vorlaeufig');
    if (containsUnvoll) {
      return {
        level: 'red',
        title: 'Quartal enthält aktuellen Monat',
        message: `Q${quarter} ${year} enthält den aktuellen Monat — Werte für diesen Monat sind unvollständig. Belastbar bis inkl. ${cutoffLabel}.`,
      };
    }
    if (containsVorl) {
      return {
        level: 'amber',
        title: 'Quartal enthält Vormonat',
        message: `Q${quarter} ${year} enthält den Vormonat — Werte für diesen Monat sind vorläufig. Belastbar bis inkl. ${cutoffLabel}.`,
      };
    }
    return null;
  }

  if (period === 'ytd') {
    if (year < v.year) {
      return null; // Vorjahres-YTD = volles Jahr, alles belastbar
    }
    if (cutoff === null) {
      return {
        level: 'red',
        title: 'Keine belastbaren Monate',
        message: `${year} hat noch keine belastbaren Monate (mindestens 2 abgeschlossene Monate erforderlich).`,
      };
    }
    return {
      level: 'info',
      title: 'YTD belastbar bis inkl. ' + cutoffLabel,
      message: `Aktueller und Vormonat sind ausgenommen (Buchungen laufen ~1 Monat nach). Berechnung umfasst ${MONTH_LABELS_DE[0]}–${MONTH_LABELS_DE[cutoff - 1]} ${year}.`,
    };
  }

  if (period === 'custom' && fromDate && toDate) {
    const toY = parseInt(toDate.slice(0, 4), 10);
    const toM = parseInt(toDate.slice(5, 7), 10);
    const m = monthMaturity(toY, toM, today);
    if (m === 'unvollstaendig') {
      return {
        level: 'red',
        title: 'Datumsbereich endet im aktuellen Monat',
        message: `Der Datumsbereich reicht bis in den aktuellen Monat ${MONTH_LABELS_DE[toM - 1]} ${toY} — Werte für diesen Monat sind unvollständig.`,
      };
    }
    if (m === 'vorlaeufig') {
      return {
        level: 'amber',
        title: 'Datumsbereich endet im Vormonat',
        message: `Der Datumsbereich reicht bis in den Vormonat ${MONTH_LABELS_DE[toM - 1]} ${toY} — Werte für diesen Monat sind vorläufig.`,
      };
    }
    return null;
  }

  return null;
}

/**
 * Adjusts a periodState to apply the YTD-cutoff:
 *   period=ytd  →  period=custom, from=YYYY-01-01, to=YYYY-MM-{last-day}
 *   sonst       →  unverändert
 * Wenn cutoff=null (z.B. heute = Januar, year=currentYear), bleibt periodState ytd
 * und liefert leere Werte — Banner warnt darüber.
 */
export function applyYtdCutoff(periodState, today = new Date()) {
  if (!periodState || periodState.period !== 'ytd') return periodState;
  const v = todayVienna(today);
  if (periodState.year < v.year) return periodState; // Vorjahr = ganzes Jahr
  const cutoff = lastMatureMonth(periodState.year, today);
  if (cutoff === null) return periodState; // kein belastbarer Monat — Banner warnt
  const mm = String(cutoff).padStart(2, '0');
  const lastDay = new Date(periodState.year, cutoff, 0).getDate();
  const dd = String(lastDay).padStart(2, '0');
  return {
    ...periodState,
    period: 'custom',
    fromDate: `${periodState.year}-01-01`,
    toDate: `${periodState.year}-${mm}-${dd}`,
    _ytdMasked: true, // Marker für PeriodToggle, damit YTD-Button selected bleibt
    _ytdCutoffMonth: cutoff,
  };
}
